import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  SocketEvent,
  DEFAULT_ADMIN_PERMISSIONS,
  type AdminPermissions,
} from '@swtorbit/shared-socket-events';
import { authStorage } from '../utils/authStorage';
import { refreshAccessToken, logout as apiLogout } from '../utils/api';
import { API_URL } from '../config';
import { authLogger } from '../utils/logger';
import { browserTimezone, updateAdminTimezone } from '../services/adminProfileApi';

interface AuthContextType {
  username: string;
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  isSuperAdmin: boolean;
  permissions: AdminPermissions;
  loginError: string | null;
  setUsername: (username: string) => void;
  login: (username: string, password: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { socket, isConnected, authenticate, registerReconnectHandler } = useSocketContext();
  const [username, setUsername] = useState(authStorage.getUsername() || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Only a stored session has anything to check; without one (first visit, or
  // the reload after logout) the login form shows straight away.
  const [isAuthenticating, setIsAuthenticating] = useState(() => authStorage.hasStoredSession());
  const [isSuperAdmin, setIsSuperAdmin] = useState(authStorage.getSuperAdmin());
  const [permissions, setPermissions] = useState<AdminPermissions>(authStorage.getPermissions());
  const [loginError, setLoginError] = useState<string | null>(null);
  
  // Use ref to avoid stale closures
  const usernameRef = useRef(username);

  useEffect(() => {
    usernameRef.current = username;
  }, [username]);

  // Login handler
  const login = useCallback(async (newUsername: string, password: string) => {
    setLoginError(null);
    setIsAuthenticating(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/login`, {
        method: 'POST',
        credentials: 'include', // Receive httpOnly cookie
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: newUsername, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const { accessToken, username: authUsername, isSuperAdmin: superAdmin, permissions: perms } = await response.json();

      // Store username (refresh token is now in httpOnly cookie)
      authStorage.setUsername(authUsername);
      authStorage.setSuperAdmin(!!superAdmin);
      authStorage.setPermissions(perms ?? DEFAULT_ADMIN_PERMISSIONS);
      setUsername(authUsername);
      setIsSuperAdmin(!!superAdmin);
      setPermissions(perms ?? DEFAULT_ADMIN_PERMISSIONS);

      // Authenticate socket with access token
      if (socket) {
        authenticate({ 
          username: authUsername, 
          clientType: 'admin',
          token: accessToken 
        });
      }
    } catch (error) {
      authLogger.error('Login error', { error });
      setLoginError(error instanceof Error ? error.message : 'Login failed');
      setIsAuthenticating(false);
    }
  }, [socket, authenticate]);

  // Logout handler
  const logout = useCallback(async () => {
    // Call server to clear httpOnly cookie and revoke token
    try {
      await apiLogout();
    } catch (error) {
      authLogger.error('Logout API error', { error });
      // Continue with local logout even if API call fails
    }
    authStorage.clearAuth();
    localStorage.removeItem('swtorbit.trl.lastArea');
    setIsAuthenticated(false);
    window.location.reload();
  }, []);

  // Once a session, offer the browser's timezone; the server keeps it only
  // while the admin has none (they set their own in Profile).
  const timezoneOffered = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || timezoneOffered.current) return;
    const timezone = browserTimezone();
    if (!timezone) return;
    timezoneOffered.current = true;
    updateAdminTimezone(timezone, true).catch((error) => authLogger.warn('Could not offer the browser timezone', { error }));
  }, [isAuthenticated]);

  // Re-authenticate with the server (used for initial login and reconnection)
  const reauthenticate = useCallback(async () => {
    const storedUsername = authStorage.getUsername();
    
    if (!storedUsername || !socket) {
      authLogger.debug('Cannot reauthenticate: no stored session or socket');
      return;
    }

    try {
      authLogger.debug('Refreshing access token...');
      const { accessToken, isSuperAdmin: superAdmin, permissions: perms } = await refreshAccessToken();
      if (superAdmin !== undefined) {
        authStorage.setSuperAdmin(superAdmin);
        setIsSuperAdmin(superAdmin);
      }
      if (perms !== undefined) {
        authStorage.setPermissions(perms);
        setPermissions(perms);
      }

      authLogger.debug('Re-authenticating socket...');
      socket.emit(SocketEvent.AUTH_REQUEST, {
        username: storedUsername,
        clientType: 'admin',
        token: accessToken,
      });
    } catch (error) {
      authLogger.error('Re-authentication failed', { error });
      // Don't clear auth on reconnect failure - might be temporary network issue
    }
  }, [socket]);

  // Register reconnection handler
  useEffect(() => {
    const unregister = registerReconnectHandler(() => {
      authLogger.info('Socket reconnected - re-authenticating...');
      reauthenticate();
    });
    
    return unregister;
  }, [registerReconnectHandler, reauthenticate]);

  // Auto-login on mount if we have a stored session (httpOnly cookie may still be valid)
  useEffect(() => {
    if (!socket || !isConnected) {
      authLogger.debug('Waiting for socket connection...');
      return;
    }

    const storedUsername = authStorage.getUsername();

    if (!storedUsername) {
      authLogger.debug('No stored session');
      setIsAuthenticating(false);
      return;
    }

    const attemptAutoLogin = async () => {
      try {
        authLogger.debug('Attempting to refresh access token via httpOnly cookie...');
        // The httpOnly cookie is automatically sent with this request
        const { accessToken, isSuperAdmin: superAdmin, permissions: perms } = await refreshAccessToken();
        if (superAdmin !== undefined) {
          authStorage.setSuperAdmin(superAdmin);
          setIsSuperAdmin(superAdmin);
        }
        if (perms !== undefined) {
          authStorage.setPermissions(perms);
          setPermissions(perms);
        }

        authLogger.debug('Authenticating with socket...');
        socket.emit(SocketEvent.AUTH_REQUEST, {
          username: storedUsername,
          clientType: 'admin',
          token: accessToken,
        });
        
        setUsername(storedUsername);
      } catch (error) {
        authLogger.error('Auto-login failed', { error });
        authStorage.clearAuth();
        setIsAuthenticating(false);
      }
    };

    attemptAutoLogin();
    // Note: reauthenticate is intentionally not a dependency here - 
    // we only want this to run once on initial connection
  }, [socket, isConnected]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    const handleAuthSuccess = () => {
      authLogger.info('Authentication successful');
      setIsAuthenticated(true);
      setIsAuthenticating(false);
      if (usernameRef.current) {
        authStorage.setUsername(usernameRef.current);
      }
      
      // Request initial data
      socket.emit(SocketEvent.ADMIN_GET_CLIENTS);
      socket.emit(SocketEvent.GAME_LIST);
    };

    const handleAuthFailure = () => {
      authLogger.warn('Authentication failed');
      setIsAuthenticated(false);
      setIsAuthenticating(false);
      authStorage.clearAuth();
    };

    socket.on(SocketEvent.AUTH_SUCCESS, handleAuthSuccess);
    socket.on(SocketEvent.AUTH_FAILURE, handleAuthFailure);

    return () => {
      socket.off(SocketEvent.AUTH_SUCCESS, handleAuthSuccess);
      socket.off(SocketEvent.AUTH_FAILURE, handleAuthFailure);
    };
  }, [socket]);

  const value = useMemo(() => ({
    username,
    isAuthenticated,
    isAuthenticating,
    isSuperAdmin,
    permissions,
    loginError,
    setUsername,
    login,
    logout,
  }), [
    username,
    isAuthenticated,
    isAuthenticating,
    isSuperAdmin,
    permissions,
    loginError,
    setUsername,
    login,
    logout,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook for consuming context
export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
